// TargetJobInformation.jsx

import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
  memo,
} from "react";
import { FormContext } from "./FormContext";
import {
  Paper,
  Snackbar,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  Tooltip,
  Grid,
  MenuItem,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Divider,
  // TextareaAutosize, // Comment out import
  useMediaQuery,
} from "@material-ui/core";
import ContextualCreditsDisplay from "./ContextualCreditsDisplay";
import { Alert, Skeleton } from "@material-ui/lab";
import { makeStyles, useTheme } from "@material-ui/core/styles"; // Import useTheme
import { SubsectionHeader, FormField, DateHelperText } from "./FormComponents";
import WorkIcon from "@material-ui/icons/Work";
import BusinessIcon from "@material-ui/icons/Business";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import DescriptionIcon from "@material-ui/icons/Description";

import FormSectionSubHeader from "./FormSectionSubHeader";
import JobParseDialog from "./JobParseDialog"; // Import the new dialog component

import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { db } from "../util/firebase";

import { useAuth } from "../util/auth";
import { useRouter } from "../util/router";

import debounce from "lodash.debounce";
// import isEqual from "lodash/isEqual"; // No longer needed for auto-save

import * as Yup from "yup";

// --- Custom Adapter to remove the inputRef prop warning --- (Commented out)
// Material-UI's TextField passes an `inputRef` prop to its custom inputComponent.
// TextareaAutosize does not expect an `inputRef` prop so we remove it and pass it
// as a ref to the underlying TextareaAutosize.
// const TextareaAutosizeAdapter = React.forwardRef(
//   function TextareaAutosizeAdapter(props, ref) {
//     const { inputRef, ...other } = props;
//     return <TextareaAutosize ref={inputRef || ref} {...other} />;
//   }
// );

/* MAX SETTINGS */
const MAX_JOB_ENTRIES = 10;
const MAX_TOOLKIT_SELECTIONS = 20;

/* STYLES (unchanged) */
const useStyles = makeStyles((theme) => ({
  formPaper: {
    backgroundColor: theme.palette.background.paper,
    overflow: "hidden",
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
  },
  formContent: {
    position: "relative",
  },
  formContainer: {
    paddingTop: theme.spacing(2),
  },
  entryContainer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(3),
    paddingBottom: theme.spacing(6),
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.grey[900]
        : theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    position: "relative",
  },
  actionButtons: {
    position: "absolute",
    right: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
  },
  checkboxGrid: {
    marginTop: theme.spacing(2),
  },
  checkboxLabel: {
    marginLeft: theme.spacing(1),
  },
  autoSaveAlert: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  applicationDeadlineField: {
    "& .MuiInputBase-root": {},
    "& .MuiInputLabel-root": {
      transform: "translate(14px, 14px) scale(1)",
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.75) !important",
    },
    "& input[type='date']::-webkit-calendar-picker-indicator": {
      color: theme.palette.primary.main,
    },
  },
  toolkitSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.grey[900]
        : theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[1],
  },
  toolkitTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  parseDialogTextArea: {
    width: "100%",
    marginTop: theme.spacing(2),
  },
  parseDialogField: {
    marginTop: theme.spacing(2),
  },
  parseDialogSection: {
    marginTop: theme.spacing(2),
  },
  parseDialogHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
}));

/* GENERATION OPTIONS */
const generationOptions = [
  {
    name: "resume",
    label: "Resume",
    tooltip: "Generate a tailored resume for this job",
  },
  {
    name: "coverLetter",
    label: "Cover Letter",
    tooltip: "Create a customized cover letter",
  },
  {
    name: "references",
    label: "References",
    tooltip: "Prepare a list of professional references",
  },
  {
    name: "messageToRecruiter",
    label: "Message to Recruiter",
    tooltip: "Generate a personalized message for LinkedIn recruiters",
  },
  {
    name: "linkedinProfileOptimization",
    label: "LinkedIn Profile Optimization",
    tooltip: "Get suggestions to improve your LinkedIn profile",
  },
  {
    name: "interviewPreparation",
    label: "Interview Preparation",
    tooltip: "Prepare for job interviews with tailored questions and answers",
  },
  {
    name: "networkingEmailTemplates",
    label: "Networking Email Templates",
    tooltip: "Create templates for networking emails",
  },
  {
    name: "careerDevelopmentPlan",
    label: "Career Development Plan",
    tooltip: "Get a personalized career development plan",
  },
  {
    name: "jobApplicationFollowUpEmail",
    label: "Job Application Follow-up Emails",
    tooltip: "Generate follow-up emails for your job application",
  },
  {
    name: "salaryNegotiationScript",
    label: "Salary Negotiation Scripts",
    tooltip: "Receive scripts for salary negotiations",
  },
  {
    name: "professionalBio",
    label: "Professional Bio Creation",
    tooltip: "Create a professional bio for various platforms",
  },
  {
    name: "personalBrandingTips",
    label: "Personal Branding Tips",
    tooltip: "Get tips for building your personal brand",
  },
  {
    name: "skillGapAnalysis",
    label: "Skill Gap Analysis",
    tooltip: "Identify and address skill gaps for this job",
  },
  {
    name: "companyResearchReports",
    label: "Company Research Reports",
    tooltip: "Get detailed reports on the target company",
  },
];

/* INITIAL OBJECT */
const initialTargetJobInfo = {
  jobTitle: "",
  companyName: "",
  jobLocation: "",
  jobType: "",
  salary: "",
  applicationDeadline: "",
  jobDescription: "",
  generationOptions: generationOptions.reduce((acc, option) => {
    acc[option.name] = false;
    return acc;
  }, {}),
};

/* FORM SECTIONS */
const formSections = [
  {
    title: "Job Details",
    fields: [
      {
        name: "jobTitle",
        label: "Job Title",
        required: true,
        icon: WorkIcon,
        maxLength: 100,
      },
      {
        name: "companyName",
        label: "Company Name",
        required: true,
        icon: BusinessIcon,
        maxLength: 100,
      },
      {
        name: "jobLocation",
        label: "Job Location",
        maxLength: 100,
      },
      {
        name: "jobType",
        label: "Job Type",
        required: true,
        select: true,
        options: [
          { value: "Full-time", label: "Full-time" },
          { value: "Part-time", label: "Part-time" },
          { value: "Contract", label: "Contract" },
          { value: "Internship", label: "Internship" },
          { value: "Temporary", label: "Temporary" },
        ],
      },
    ],
  },
  {
    title: "Salary and Deadline",
    fields: [
      {
        name: "salary",
        label: "Salary",
        type: "text",
      },
      {
        name: "applicationDeadline",
        label: "Application Deadline",
        type: "date",
        helperText: "Enter the deadline for submitting your application",
        InputLabelProps: { shrink: true },
      },
    ],
  },
  {
    title: "Job Description",
    fields: [
      {
        name: "jobDescription",
        label: "Job Description",
        required: true,
        multiline: true,
        fullWidth: true,
        maxLength: 8000,
        // Remove custom InputProps to use default TextField multiline behavior
        // InputProps: {
        //   inputComponent: TextareaAutosizeAdapter,
        //   inputProps: {
        //     minRows: 10,
        //   },
        // },
        minRows: 10, // Use TextField's built-in minRows prop instead
      },
    ],
  },
];

/* YUP SCHEMA */
export const targetJobSchema = Yup.object().shape({
  jobTitle: Yup.string()
    .required("Job Title is required")
    .max(100, "Job Title cannot exceed 100 characters"),
  companyName: Yup.string()
    .required("Company Name is required")
    .max(100, "Company Name cannot exceed 100 characters"),
  jobLocation: Yup.string().max(
    100,
    "Job Location cannot exceed 100 characters"
  ),
  jobType: Yup.string()
    .required("Job Type is required")
    .oneOf(
      ["Full-time", "Part-time", "Contract", "Internship", "Temporary"],
      "Invalid Job Type"
    ),
  salary: Yup.string().max(100, "Salary cannot exceed 100 characters"),
  applicationDeadline: Yup.date()
    .nullable()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .min(new Date(), "Application Deadline cannot be in the past"),
  jobDescription: Yup.string()
    .required("Job Description is required")
    .max(8000, "Job Description cannot exceed 8000 characters"),
  generationOptions: Yup.object().shape(
    generationOptions.reduce((acc, option) => {
      acc[option.name] = Yup.boolean();
      return acc;
    }, {})
  ),
});

export const formSchema = Yup.object().shape({
  targetJobInformation: Yup.array()
    .of(targetJobSchema)
    .min(1, "At least one target job must be added")
    .max(
      MAX_JOB_ENTRIES,
      `You can only add up to ${MAX_JOB_ENTRIES} target jobs`
    ),
});

/* Normalizes job data: ensures it's an array and generationOptions exist */
const normalizeJobData = (data) => {
  const jobs = Array.isArray(data) ? data : data ? [data] : []; // Ensure data is an array
  if (jobs.length === 0) {
    return [{ ...initialTargetJobInfo }]; // Ensure at least one entry if empty
  }
  // Map over jobs, ensuring each job has default structure and generationOptions
  return jobs.map((job) => {
    const currentJob = job || {}; // Handle null/undefined job entries
    const jobGenerationOptions =
      typeof currentJob.generationOptions === "object" &&
      currentJob.generationOptions !== null
        ? currentJob.generationOptions
        : {};
    return {
      ...initialTargetJobInfo, // Start with defaults
      ...currentJob, // Spread loaded job data
      generationOptions: {
        ...initialTargetJobInfo.generationOptions, // Start with default options
        ...jobGenerationOptions, // Override with loaded options
      },
    };
  });
};

/* Child component for a single Target Job entry */
// Remove memo for debugging potential update issues
// const TargetJobEntry = memo(
const TargetJobEntry = ({
  index,
  jobInfo,
  handleFieldChange,
  handleOptionChange,
  handleRemoveEntry,
  handleAddEntry,
  errors,
  touched,
  userCredits,
  countToolkitSelections,
  handleOpenJobParseDialog,
}) => {
  const classes = useStyles();

  return (
    <Box className={classes.entryContainer}>
      {formSections.map((section) => (
        <React.Fragment key={section.title}>
          <Box display="flex" alignItems="center">
            <SubsectionHeader title={section.title} />
            {section.title === "Job Details" && (
              <Box ml={2}>
                <Tooltip title="Extract from Job Description">
                  <IconButton
                    onClick={() => handleOpenJobParseDialog(index)}
                    color="primary"
                    aria-label="Parse job description"
                    style={{ marginBottom: "4px" }}
                  >
                    <DescriptionIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>
          <Grid container spacing={3}>
            {section.fields.map((field) => (
              <Grid item xs={12} sm={field.fullWidth ? 12 : 6} key={field.name}>
                <FormField
                  name={field.name}
                  label={field.label}
                  required={field.required}
                  value={jobInfo[field.name] ?? ""}
                  onChange={(name, value) =>
                    handleFieldChange(index, name, value)
                  }
                  error={
                    touched &&
                    touched[field.name] &&
                    errors &&
                    errors[field.name]
                  }
                  helperText={
                    touched &&
                    touched[field.name] &&
                    errors &&
                    errors[field.name]
                  }
                  tooltip={`Enter the ${field.label.toLowerCase()}`}
                  aria-label={field.label}
                  placeholder={
                    field.type === "date"
                      ? "mm/dd/yyyy"
                      : `Enter ${field.label.toLowerCase()}`
                  }
                  maxLength={field.maxLength}
                  type={field.type}
                  multiline={field.multiline}
                  rows={field.rows}
                  fullWidth
                  select={field.select}
                  options={field.options}
                  icon={field.icon ? <field.icon /> : null}
                  InputLabelProps={
                    field.type === "date" ? field.InputLabelProps : undefined
                  }
                  className={
                    field.name === "applicationDeadline"
                      ? classes.applicationDeadlineField
                      : undefined
                  }
                  InputProps={field.InputProps} // Pass InputProps to FormField
                >
                  {field.select &&
                    field.options.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                </FormField>
                {field.type === "date" && (
                  <DateHelperText text={field.helperText} />
                )}
              </Grid>
            ))}
          </Grid>
        </React.Fragment>
      ))}

      <Paper className={classes.toolkitSection}>
        <ContextualCreditsDisplay
          showInToolkit={true}
          toolkitTitle="Job Application Toolkit"
          selectedCount={
            Object.values(jobInfo.generationOptions || {}).filter(Boolean)
              .length
          }
        />
        <Typography variant="body2" color="textSecondary">
          Select the resources and documents you'd like to generate for this job
          application. Each selection will use one credit.
        </Typography>
        <Grid container spacing={2} className={classes.checkboxGrid}>
          {generationOptions.map((option) => {
            const isOptionSelected = Boolean(
              jobInfo.generationOptions[option.name]
            );
            return (
              <Grid item xs={12} sm={6} md={4} key={option.name}>
                <Tooltip title={option.tooltip} placement="top">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isOptionSelected}
                        onChange={(e) =>
                          handleOptionChange(
                            index,
                            option.name,
                            e.target.checked
                          )
                        }
                        name={option.name}
                        color="primary"
                        aria-label={option.label}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        className={classes.checkboxLabel}
                      >
                        {option.label}
                      </Typography>
                    }
                  />
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      <Box className={classes.actionButtons}>
        <Tooltip title="Remove this target job">
          <IconButton
            onClick={() => handleRemoveEntry(index)}
            color="secondary"
            aria-label="Remove target job"
          >
            <RemoveCircleOutlineIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Add another target job">
          <IconButton
            onClick={handleAddEntry}
            color="primary"
            aria-label="Add target job"
          >
            <AddCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
// ); // End of memo wrapper

/* MAIN COMPONENT */
export const TargetJobInformation = () => {
  const classes = useStyles();
  const theme = useTheme(); // Get theme object
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { formData, updateFormData } = useContext(FormContext);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [alertMessage, setAlertMessage] = useState(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null);
  // const [loading, setLoading] = useState(true); // Remove internal loading state
  const [userCredits, setUserCredits] = useState(0);
  const [purchaseCreditsDialogOpen, setPurchaseCreditsDialogOpen] =
    useState(false);

  // State for controlling the NEW JobParseDialog
  const [isParseDialogOpen, setIsParseDialogOpen] = useState(false);
  const [parsingJobIndex, setParsingJobIndex] = useState(null); // Index of the job being parsed

  const auth = useAuth();
  const router = useRouter();

  // Ref to track mounted status
  const isMountedRef = useRef(true);
  useEffect(() => {
    // Set ref to false when component unmounts
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Removed direct localStorage and Firestore saving from this component.
  // FormContext is now the single source of truth for persistence.

  /* On mount, ensure targetJobInformation in FormContext is correctly initialized */
  useEffect(() => {
    if (
      formData &&
      (!formData.targetJobInformation ||
        !Array.isArray(formData.targetJobInformation) ||
        formData.targetJobInformation.length === 0)
    ) {
      console.log(
        "[TargetJobInfo] Initializing targetJobInformation in FormContext"
      );
      updateFormData("targetJobInformation", [{ ...initialTargetJobInfo }]);
    } else if (formData && formData.targetJobInformation) {
      // Optional: Could add a one-time normalization if concerned about old structures from direct localStorage reads
      // For now, assume FormContext's initial load handles basic structure.
      // We could also re-normalize here if needed:
      // const normalized = normalizeJobData(formData.targetJobInformation);
      // if (!isEqual(formData.targetJobInformation, normalized)) { // lodash/isEqual needed if using this
      //   updateFormData("targetJobInformation", normalized);
      // }
    }
  }, [formData, updateFormData]); // Rerun if formData or updateFormData changes

  /* Listen for user credits from Firestore */
  useEffect(() => {
    let unsubscribe;
    if (auth.user) {
      const userDocRef = doc(db, "users", auth.user.uid);
      unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
        if (isMountedRef.current) {
          // Check before setting state
          if (docSnapshot.exists()) {
            const data = docSnapshot.data();
            setUserCredits(data.credits || 0);
          } else {
            setUserCredits(0);
          }
        }
      });
    } else {
      if (isMountedRef.current) {
        // Check before setting state
        setUserCredits(0);
      }
    }
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [auth.user]);

  // Removed auto-save useEffect as FormContext handles saving the entire formData

  /* Count how many generationOptions are checked across all jobs */
  const countToolkitSelections = () => {
    const targetJobs = Array.isArray(formData.targetJobInformation)
      ? formData.targetJobInformation
      : [];
    return targetJobs.reduce((total, job) => {
      const genOpts = job.generationOptions || {};
      return total + Object.values(genOpts).filter(Boolean).length;
    }, 0);
  };

  /* Handle field changes (jobTitle, jobType, etc.) */
  const handleFieldChange = async (index, name, value) => {
    // Ensure targetJobInformation is an array
    const targetJobInfo = Array.isArray(formData.targetJobInformation)
      ? formData.targetJobInformation
      : [{ ...initialTargetJobInfo }];

    // Create a copy of the job at the specified index, or use initialTargetJobInfo if it doesn't exist
    const updatedJob = {
      ...(targetJobInfo[index] || initialTargetJobInfo),
      [name]: value,
    };

    // Create a copy of the array
    const updatedJobs = [...targetJobInfo];
    updatedJobs[index] = updatedJob;

    // Update the form data
    updateFormData("targetJobInformation", updatedJobs);

    // Validate with Yup
    try {
      await targetJobSchema.validateAt(name, updatedJob);
      if (isMountedRef.current) {
        // Check before setting state
        setErrors((prev) => {
          const jobErrors = prev[index] || {};
          delete jobErrors[name];
          return { ...prev, [index]: jobErrors };
        });
        setTouched((prev) => ({
          ...prev,
          [index]: { ...(prev[index] || {}), [name]: true },
        }));
      }
    } catch (validationError) {
      if (isMountedRef.current) {
        // Check before setting state
        setErrors((prev) => {
          const jobErrors = prev[index] || {};
          return {
            ...prev,
            [index]: { ...jobErrors, [name]: validationError.message },
          };
        });
        setTouched((prev) => ({
          ...prev,
          [index]: { ...(prev[index] || {}), [name]: true },
        }));
      }
    }
  };

  /* Handle checking/unchecking generationOptions */
  const handleOptionChange = (index, optionName, checked) => {
    const totalSelections = countToolkitSelections();

    if (checked) {
      if (totalSelections >= MAX_TOOLKIT_SELECTIONS) {
        if (isMountedRef.current) {
          setAlertMessage(
            `You can only select up to ${MAX_TOOLKIT_SELECTIONS} toolkit options in total.`
          );
        }
        return;
      }

      const estimatedCreditsRemaining = userCredits - (totalSelections + 1);
      if (estimatedCreditsRemaining < 0) {
        if (isMountedRef.current) {
          setPurchaseCreditsDialogOpen(true);
        }
        return;
      }
    }

    // Ensure targetJobInformation is an array
    const targetJobInfo = Array.isArray(formData.targetJobInformation)
      ? formData.targetJobInformation
      : [{ ...initialTargetJobInfo }];

    // Get the current job and its generation options
    const currentJob = targetJobInfo[index] || { ...initialTargetJobInfo };
    const currentGenOpts = currentJob.generationOptions || {};

    // Create updated job with new option value
    const updatedJob = {
      ...currentJob,
      generationOptions: {
        ...currentGenOpts,
        [optionName]: checked,
      },
    };

    // Create a copy of the array and update the job at the specified index
    const updatedJobs = [...targetJobInfo];
    updatedJobs[index] = updatedJob;

    // Update the form data
    updateFormData("targetJobInformation", updatedJobs);
  };

  /* Add an entry if under MAX */
  const handleAddEntry = () => {
    // Ensure targetJobInformation is an array
    const targetJobInfo = Array.isArray(formData.targetJobInformation)
      ? formData.targetJobInformation
      : [{ ...initialTargetJobInfo }];

    if (targetJobInfo.length >= MAX_JOB_ENTRIES) {
      if (isMountedRef.current) {
        setAlertMessage(
          `You can only add up to ${MAX_JOB_ENTRIES} target jobs.`
        );
      }
      return;
    }

    updateFormData("targetJobInformation", [
      ...targetJobInfo,
      { ...initialTargetJobInfo },
    ]);
  };

  /* Remove an entry if more than 1 left */
  const handleRemoveEntry = (index) => {
    // Ensure targetJobInformation is an array
    const targetJobInfo = Array.isArray(formData.targetJobInformation)
      ? formData.targetJobInformation
      : [{ ...initialTargetJobInfo }];

    if (targetJobInfo.length === 1) {
      if (isMountedRef.current) {
        setAlertMessage("You must have at least one target job.");
      }
      return;
    }

    const updatedJobs = targetJobInfo.filter((_, i) => i !== index);
    updateFormData("targetJobInformation", updatedJobs);

    // Remove errors for that entry & re-index
    if (isMountedRef.current) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[index];
        const reIndexed = {};
        updatedJobs.forEach((_, i) => {
          reIndexed[i] = newErrors[i >= index ? i + 1 : i];
        });
        return reIndexed;
      });
      // Remove touched for that entry & re-index
      setTouched((prev) => {
        const newTouched = { ...prev };
        delete newTouched[index];
        const reIndexedTouched = {};
        updatedJobs.forEach((_, i) => {
          reIndexedTouched[i] = newTouched[i >= index ? i + 1 : i];
        });
        return reIndexedTouched;
      });
    }
  };

  // --- Job Parsing Dialog Handlers ---

  /* Opens the new JobParseDialog */
  const openParseDialog = (index) => {
    setParsingJobIndex(index);
    setIsParseDialogOpen(true);
  };

  /* Closes the new JobParseDialog */
  const closeParseDialog = () => {
    setIsParseDialogOpen(false);
    setParsingJobIndex(null);
  };

  /* Handles confirmed data from the JobParseDialog */
  const handleConfirmParse = (parsedDetails) => {
    if (parsingJobIndex === null) return; // Should not happen, but safety check

    const updatedJobs = [...formData.targetJobInformation];
    const currentJob = updatedJobs[parsingJobIndex];

    // Merge parsed details, explicitly clearing fields if not found in the new parse
    const updatedJob = {
      ...currentJob, // Start with existing data
      jobTitle: parsedDetails.jobTitle ?? "", // Use parsed or clear
      companyName: parsedDetails.companyName ?? "", // Use parsed or clear
      jobLocation: parsedDetails.jobLocation ?? "", // Use parsed or clear
      jobType: parsedDetails.jobType ?? "", // Use parsed or clear
      salary: parsedDetails.salary ?? "", // Use parsed or clear
      applicationDeadline: parsedDetails.applicationDeadline ?? "", // Use parsed or clear
      // Keep original description if parse fails, otherwise use parsed or clear
      jobDescription: parsedDetails.jobDescription ?? "",
    };

    updatedJobs[parsingJobIndex] = updatedJob;
    updateFormData("targetJobInformation", updatedJobs);

    // No need to call closeParseDialog here, as the dialog closes itself after onConfirm
  };

  /* Always ensure at least 1 job is present */
  const targetJobInformation = Array.isArray(formData.targetJobInformation)
    ? formData.targetJobInformation.length > 0
      ? formData.targetJobInformation
      : [{ ...initialTargetJobInfo }]
    : [{ ...initialTargetJobInfo }];

  return (
    <Paper elevation={3} className={classes.formPaper}>
      {!isMobile && <FormSectionSubHeader title="Target Job Information:" />}
      <Box className={classes.formContent}>
        <Box
          style={{
            padding: theme.spacing(1, 0),
            marginBottom: theme.spacing(2),
          }}
        >
          <Typography
            variant="body2"
            color="textSecondary"
            style={{
              fontStyle: "italic",
              display: "flex",
              alignItems: "center",
            }}
          >
            <span style={{ marginRight: theme.spacing(1) }}>💡</span>
            <span>
              Adding the job description helps our AI match your materials to
              what employers are looking for, increasing your chances of getting
              an interview.
            </span>
          </Typography>
        </Box>
        {/* Removed internal loading check, rely on Suspense */}
        <div className={classes.formContainer}>
          {targetJobInformation.map((jobInfo, index) => (
            <React.Fragment key={index}>
              <TargetJobEntry
                index={index}
                jobInfo={jobInfo}
                handleFieldChange={handleFieldChange}
                handleOptionChange={handleOptionChange}
                handleRemoveEntry={handleRemoveEntry}
                handleAddEntry={handleAddEntry}
                errors={errors[index] || {}}
                touched={touched[index] || {}}
                userCredits={userCredits}
                countToolkitSelections={countToolkitSelections}
                handleOpenJobParseDialog={openParseDialog} // Pass the new handler
              />
              {/* Render Divider only if it's NOT the last item */}
              {index < targetJobInformation.length - 1 && (
                <Divider style={{ margin: `${theme.spacing(4)}px 0` }} />
              )}
            </React.Fragment>
          ))}
        </div>
        {/* Removed closing parenthesis and brace for the loading check */}
      </Box>

      {/* Snackbar for general alerts */}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={!!alertMessage}
        autoHideDuration={6000}
        onClose={() => {
          if (isMountedRef.current) setAlertMessage(null);
        }} // Check on close
      >
        <Alert
          onClose={() => {
            if (isMountedRef.current) setAlertMessage(null);
          }}
          severity="warning"
        >
          {alertMessage}
        </Alert>
      </Snackbar>

      {/* Snackbar for auto-save status */}
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        open={Boolean(autoSaveStatus)}
        autoHideDuration={2000}
        onClose={() => {
          if (isMountedRef.current) setAutoSaveStatus(null);
        }} // Check on close handler too
      >
        <Alert
          onClose={() => {
            if (isMountedRef.current) setAutoSaveStatus(null);
          }} // Check on close handler too
          severity={
            autoSaveStatus === "error"
              ? "error"
              : autoSaveStatus === "success"
              ? "success"
              : "info"
          }
          variant="filled"
          className={classes.autoSaveAlert}
        >
          {autoSaveStatus === "saving"
            ? "Auto-saving..."
            : autoSaveStatus === "success"
            ? "All changes saved"
            : "Auto-save failed"}
        </Alert>
      </Snackbar>

      {/* Dialog for insufficient credits */}
      <Dialog
        open={purchaseCreditsDialogOpen}
        onClose={() => setPurchaseCreditsDialogOpen(false)}
      >
        <DialogTitle>Not Enough Credits</DialogTitle>
        <DialogContent>
          <Typography>
            You don't have enough credits to select this option. Please purchase
            more credits to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPurchaseCreditsDialogOpen(false)}
            color="default"
            variant="outlined"
          >
            Continue Editing
          </Button>
          <Button
            onClick={() => {
              setPurchaseCreditsDialogOpen(false);
              router.push("/pricing");
            }}
            color="primary"
            variant="contained"
          >
            Purchase Credits
          </Button>
        </DialogActions>
      </Dialog>

      {/* Render the extracted JobParseDialog component */}
      <JobParseDialog
        open={isParseDialogOpen}
        onClose={closeParseDialog}
        onConfirm={handleConfirmParse}
      />
    </Paper>
  );
};

export default TargetJobInformation;
